import { connect } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Container from "react-bootstrap/Container";
import EmojiPicker from 'emoji-picker-react';

import AISettings from './dashboard/AISettings';

const LeagueContextPage = ({ auth }) => {
    const history = useHistory();
    const [leagueContext, setLeagueContext] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [teamNames, setTeamNames] = useState([]);
    const [isInitializing, setIsInitializing] = useState(true);
    const [showExamples, setShowExamples] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [platform_league_id, setPlatform_league_id] = useState(null);

    const maxCharacters = 10000;

    const loadExistingLeagueContext = useCallback(async () => {
        try {
            // Get platform_league_id from database
            if (auth?.user?.id) {
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const leagueId = response.data.platform_league_id;
                    setPlatform_league_id(leagueId);
                    
                    // Fetch league context from database
                    const contextResponse = await axios.get(`/api/get/league-context/${leagueId}`);
                    if (contextResponse.data.success && contextResponse.data.league_context) {
                        setLeagueContext(contextResponse.data.league_context);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading existing league context:', error);
        }
    }, [auth?.user?.id]);

    const fetchTeamNames = useCallback(async () => {
        try {
            // Get league data from database
            if (auth?.user?.id) {
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const teams = response.data.teams || [];
                    setTeamNames(teams);
                }
            }
        } catch (error) {
            console.error('Error fetching team names:', error);
        }
    }, [auth?.user?.id]);

    useEffect(() => {
        // Load existing league context data
        loadExistingLeagueContext();

        // Fetch team names for examples
        fetchTeamNames();
        
        // Mark initialization as complete after all checks
        setIsInitializing(false);
    }, [loadExistingLeagueContext, fetchTeamNames]);

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
                setShowEmojiPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    const handleTextChange = (e) => {
        const value = e.target.value;
        if (value.length <= maxCharacters) {
            setLeagueContext(value);
        }
    };

    const formatText = (type) => {
        const textarea = document.getElementById('leagueContextTextarea');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = leagueContext.substring(start, end);
        
        let formattedText = '';
        
        switch (type) {
            case 'bold':
                formattedText = `**${selectedText}**`;
                break;
            case 'bullet':
                if (selectedText) {
                    // Add bullet to selected text
                    formattedText = selectedText.split('\n').map(line => 
                        line.trim() ? `• ${line.trim()}` : line
                    ).join('\n');
                } else {
                    // Add bullet at cursor position
                    formattedText = '• ';
                }
                break;
            default:
                formattedText = selectedText;
        }
        
        const newText = leagueContext.substring(0, start) + formattedText + leagueContext.substring(end);
        setLeagueContext(newText);
        
        // Restore cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
        }, 0);
    };

    const onEmojiClick = (emojiObject) => {
        const textarea = document.getElementById('leagueContextTextarea');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const emoji = emojiObject.emoji;
        
        const newText = leagueContext.substring(0, start) + emoji + leagueContext.substring(end);
        setLeagueContext(newText);
        
        // Restore cursor position after emoji
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        }, 0);
        
        setShowEmojiPicker(false);
    };

    const getExampleText = () => {
        if (teamNames.length >= 2) {
            const team1 = teamNames[0]?.team_name || 'Team 1';
            const team2 = teamNames[1]?.team_name || 'Team 2';
            return `**League Drama:** ${team1} is run by John and he's brothers with ${team2}. ${team1} talks a lot of crap and he's a software engineer who's always trying to make shady trades.

• **Rivalry:** The ${team1} vs ${team2} matchup is always intense
• **Inside Jokes:** We call our league "The Salt Mines" because everyone complains about bad beats
• **Traditions:** Loser buys pizza for the winner's party`;
        }
        
        return `**League Drama:** [Team 1 Name] is run by John and he's brothers with [Team 2 Name]. [Team 1 Name] talks a lot of crap and he's a software engineer who's always trying to make shady trades.

• **Rivalry:** The [Team 1 Name] vs [Team 2 Name] matchup is always intense  
• **Inside Jokes:** We call our league "The Salt Mines" because everyone complains about bad beats
• **Traditions:** Loser buys pizza for the winner's party`;
    };

    const getRemainingCharacters = () => {
        return maxCharacters - leagueContext.length;
    };

    const handleSave = async () => {
        if (!leagueContext.trim()) {
            alert('Please enter some league context before saving.');
            return;
        }

        setIsSaving(true);

        try {
            // Get platform_league_id from database
            const response = await axios.get('/api/get/user-league-data');
            if (response.data.success) {
                const platform_league_id = response.data.platform_league_id;
                
                await axios.post('/api/post/league-context', {
                    league_context: leagueContext.trim(),
                    platform_league_id: platform_league_id
                });
                
                // Redirect to dashboard after successful save
                history.push('/dashboard');
            }
        } catch (error) {
            console.error('Error saving league context:', error);
            alert('Failed to save league context. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Show loading state while initializing
    if (isInitializing) {
        return (
            <div className="pt-5 league-context-section">    
                <Row className="league-context-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                        <Row className="text-center league-context-title-row">
                            <Col className="league-context-title">Loading League Context</Col>
                        </Row>

                        <Row className="pt-4">
                            <Col>
                                <div className="text-center">
                                    <Spinner animation="border" role="status" className="mb-3">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <h4 className="text-white">Processing...</h4>
                                </div>
                            </Col>
                        </Row>                        
                    </Col>                            
                </Row>
            </div>
        );
    }

    return (
        <>
            <div className="dashboard-container">
                <Container fluid className="px-4">
                    {/* AI Settings */}
                    <Row className="mt-5 mb-2">
                        <Col>
                            <AISettings 
                                auth={auth} 
                                platform_league_id={platform_league_id} 
                            />
                        </Col>
                    </Row>

                    {/* League Context Form */}
                    <Row className="mb-4">
                        <Col xs={1} md={2}></Col>
                        <Col xs={10} md={8}>       

                        {/* Examples Section */}
                        {showExamples && (
                            <Row className="mt-2">
                                <Col>
                                    <div className="league-context-examples">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="examples-title mb-0">💡 Example</h6>
                                            <Button 
                                                variant="link" 
                                                size="sm" 
                                                className="text-muted p-0"
                                                onClick={() => setShowExamples(false)}
                                            >
                                                Hide
                                            </Button>
                                        </div>
                                        <div className="examples-content">
                                            <div dangerouslySetInnerHTML={{ 
                                                __html: getExampleText()
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/• /g, '• ')
                                                    .replace(/\n/g, '<br/>')
                                            }} />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {!showExamples && (
                            <Row className="mt-2">
                                <Col>
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="text-muted p-0"
                                        onClick={() => setShowExamples(true)}
                                    >
                                        💡 Show example
                                    </Button>
                                </Col>
                            </Row>
                        )}

                        {/* Formatting Toolbar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="formatting-toolbar">
                                    <div className="toolbar-group">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => formatText('bold')}
                                            title="Bold selected text"
                                        >
                                            <strong>B</strong>
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => formatText('bullet')}
                                            title="Add bullet point"
                                        >
                                            ☰
                                        </Button>
                                    </div>
                                    <div className="toolbar-group emoji-picker-container">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            title="Add emoji"
                                        >
                                            😊
                                        </Button>
                                        {showEmojiPicker && (
                                            <div className="emoji-picker-wrapper">
                                                <EmojiPicker
                                                    onEmojiClick={onEmojiClick}
                                                    width={300}
                                                    height={400}
                                                    searchPlaceholder="Search emojis..."
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Text Input */}
                        <Row className="mt-3">
                            <Col>
                                <Form.Group>
                                    <Form.Control
                                        as="textarea"
                                        id="leagueContextTextarea"
                                        rows={8}
                                        value={leagueContext}
                                        onChange={handleTextChange}
                                        placeholder={`Tell us about your league members, rivalries, inside jokes, or anything that makes your league unique and your recap more personal...

[Team 1 Name] is run by John and he's brothers with [Team 2 Name]. [Team 1 Name] talks a lot of crap and he's a software engineer who's always trying to make shady trades.`}
                                        className="league-context-textarea"
                                    />
                                    
                                    {/* Character count - all screen sizes */}
                                    <div className="mt-2 text-end">
                                        <small className={`character-counter ${getRemainingCharacters() < 50 ? 'text-warning' : 'text-muted'}`}>
                                            {getRemainingCharacters()} characters remaining
                                        </small>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className='align-items-center mt-4 justify-content-center'>
                            <Col xs="auto">
                                <Button 
                                    onClick={handleSave}
                                    disabled={isSaving || !leagueContext.trim()}
                                    className={`btn-lg mx-2 onboarding-nav-btn ${leagueContext.trim() ? 'btn-primary-gradient' : 'btn-secondary'}`}
                                >
                                    {isSaving ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save'
                                    )}
                                </Button>
                            </Col>
                        </Row>                                                 
                    </Col>                            
                </Row>
                </Container>
            </div>
        </>
    );
};

function mapStateToProps({ auth }) {
    return { auth };
}

export default connect(mapStateToProps)(LeagueContextPage);
